import type { DoorPhase, GameState } from '@full-elevator/sim';
import { inLastSeconds } from '../view/feedback.js';

/**
 * What the game should sound like this frame, read off the state (v0.8).
 *
 * The view may read state freely and invent nothing, so sounds are only ever
 * *changes* the sim already made: the doors starting to open, someone getting
 * out where they wanted, someone lost. Nothing here decides anything.
 */
export type SoundEvent = 'doors' | 'delivered' | 'lost' | 'siren' | 'hurry' | 'power';

export interface Watch {
  readonly doorPhase: DoorPhase;
  /** The doors of the rest of the bank (ADR-0034), in order; empty with one car. */
  readonly moreDoors: readonly DoorPhase[];
  readonly delivered: number;
  readonly lost: number;
  /** Crews in the building (ADR-0029): one more is a siren. */
  readonly crews: number;
  /** Who is in the last seconds of their clock: someone new there is a tick. */
  readonly hurrying: ReadonlySet<string>;
  /** When a power-up was last used (ADR-0055), or -1. */
  readonly powerAt: number;
  /** Charges held: one more is a chime. */
  readonly charges: number;
}

export function watch(state: GameState): Watch {
  return {
    doorPhase: state.elevator.doorPhase,
    moreDoors: state.extraCars.map((car) => car.doorPhase),
    delivered: state.score.delivered,
    lost: state.score.abandoned + state.score.couriersLate,
    crews: state.passengers.filter((p) => p.callout !== null && p.state !== 'unspawned').length,
    hurrying: new Set(state.passengers.filter((p) => inLastSeconds(state, p)).map((p) => p.id)),
    powerAt: state.power.last?.atMs ?? -1,
    charges: state.power.charges,
  };
}

/** The changes between the last frame and this one, as sounds. */
export function soundEvents(previous: Watch | null, state: GameState): SoundEvent[] {
  const now = watch(state);
  if (!previous) return [];
  const events: SoundEvent[] = [];
  const opened =
    (now.doorPhase === 'opening' && previous.doorPhase !== 'opening') ||
    now.moreDoors.some((phase, i) => phase === 'opening' && previous.moreDoors[i] !== 'opening');
  if (opened) events.push('doors');
  if (now.delivered > previous.delivered) events.push('delivered');
  if (now.lost > previous.lost) events.push('lost');
  if (now.crews > previous.crews) events.push('siren');
  if ([...now.hurrying].some((id) => !previous.hurrying.has(id))) events.push('hurry');
  if (now.powerAt !== previous.powerAt || now.charges > previous.charges) events.push('power');
  return events;
}
